import { useState, useEffect, RefObject } from 'react';
import queryString from 'query-string';
import isEqual from 'lodash.isequal';
import { useAppDispatch } from '@store';
import {
  Filters,
  formatSearchParams,
  Pagination,
  PermissionEnum,
  SchemaFilterType,
  SelectItemProps,
  useFilters,
  useGetList,
  usePrevious,
  useQueryState,
  useScroll,
} from '@ui/src';
import { useLazyGetIndividualUsersQuery } from '../../store/services/individual';
import { useGetNewApplicationsValidatorsQuery } from '../../../new-application/store/services';
import {
  customersListFiltersSchema,
  customersListSchema,
} from '../variables/list';
import { useLazyGetCustomersApplicationSchemesQuery } from '../../../../store/dictionaries/services';
import { clearGeneralState } from '../../store/slices/general-info';
import { clearTopUpOptions } from '../../../common/store/slices/top-up-panel';
import { clearOverviewState } from '../../store/slices/overview';
import { clearDocumentsState } from '../../../common/store/slices/documents/documents';
import {
  clearAccountsTransactionState,
  clearCardsTransactionState,
} from '../../../common/store/slices/statements';
import { clearStatisticState } from '../../../common/store/slices/statistics';
import { clearTariffsState } from '../../../common/store/slices/tariff/tariff';
import { clearDevicesState } from '../../store/slices/devices';
import { clearExternalAccounts } from '../../../common/store/slices/external-integration';
import {
  clearLocksState,
  clearLogsState,
  setOrganizationId,
} from '../../store/slices/security';
import {
  BaseInfoFormValues,
  BaseUserInformation,
  GeneralInfo,
  GeneralUserInfo,
} from '../../types';
import { clearIndividualList } from '../../store/slices/list';
import { clearAmlState } from '../../../common/store/slices/aml';
import { CustomerProps, ApplicationScheme } from '../../../common/types';

let timeout: NodeJS.Timeout;

const useIndividualList = ({
  pagination,
  countries,
  scrollRef,
  baseInformation,
  generalUserInfo,
  baseUserInformation,
  applicationSchemes,
  activePermissions,
  individualCustomers,
  search,
}: {
  pagination?: Pagination;
  countries?: SelectItemProps[];
  scrollRef: RefObject<HTMLDivElement | null>;
  baseInformation?: Partial<GeneralInfo>;
  generalUserInfo: {
    isLoading: boolean;
    values?: GeneralUserInfo;
    formValues?: BaseInfoFormValues;
  };
  baseUserInformation?: Partial<GeneralUserInfo> & BaseUserInformation;
  applicationSchemes?: ApplicationScheme[];
  activePermissions?: PermissionEnum[];
  individualCustomers?: CustomerProps[];
  search?: string;
}) => {
  const [countriesList, setCountriesList] = useState<
    SelectItemProps[] | undefined
  >(undefined);
  const [nameSearch, setNameSearch] = useState('');
  const [updateSelectedListVisible, setUpdateSelectedListVisible] =
    useState(false);
  const [updateAllListVisible, setUpdateAllListVisible] = useState(false);
  const [action, setAction] = useState('');
  const [popupVisible, setPopupVisible] = useState(false);

  const dispatch = useAppDispatch();

  const { filters, onFilterChange } = useQueryState(customersListSchema);

  const prevFilters = usePrevious(filters);
  const prevPagination = usePrevious(pagination);

  useGetNewApplicationsValidatorsQuery();

  const [getCustomersApplicationSchemes] =
    useLazyGetCustomersApplicationSchemesQuery();
  const [getIndividualUserList, { isFetching, isLoading }] =
    useLazyGetIndividualUsersQuery();

  const { getListAction, onFilter, setPage, errorList } =
    useGetList<CustomerProps>({
      getList: getIndividualUserList,
      onFilterChange,
      onSortChange: () => {},
      schema: customersListSchema,
    });

  const onSearchAutocomplete = (name: string, value: string) => {
    const nextCountries = countries?.filter((item) =>
      item.name.toLowerCase().includes(value.toLowerCase()),
    );

    setCountriesList(nextCountries);
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: customersListFiltersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  useScroll({ element: scrollRef, requestInProgress: isFetching });

  const onClear = () => {
    dispatch(clearOverviewState());
    dispatch(clearAccountsTransactionState());
    dispatch(clearCardsTransactionState());
    dispatch(clearStatisticState());
    dispatch(clearDocumentsState());
    dispatch(clearTariffsState());
    dispatch(clearAmlState());
    dispatch(clearExternalAccounts());
    dispatch(clearDevicesState());
    dispatch(clearLocksState());
    dispatch(clearLogsState());
    dispatch(setOrganizationId(''));
  };

  const onCustomerSearch = (searchString: string) => {
    clearTimeout(timeout);

    setNameSearch(searchString);

    const nextFilters: Filters = { ...filters };

    if (searchString) {
      nextFilters.search = {
        value: [searchString],
        type: SchemaFilterType.STRING,
      };
    } else if (!searchString && nextFilters.search) {
      delete nextFilters.search;
    }

    timeout = setTimeout(() => {
      onFilter({ ...nextFilters });
    }, 500);
  };

  const onTableFilterChange = (values: Filters) => {
    const nextValues = { ...values };

    if (nameSearch) {
      nextValues.search = {
        value: [nameSearch],
        type: SchemaFilterType.STRING,
      };
    }

    if (values.type) {
      if (!nextValues.status) {
        nextValues.status = {
          value: ['ACTIVE', 'CLOSED'],
          type: SchemaFilterType.STRING,
        };
      }

      onFilter(nextValues);
    } else {
      const nextFilters: Filters = {
        type: {
          value: ['INDIVIDUAL', 'COMPANY_OFFICIAL'],
          type: SchemaFilterType.STRING,
        },
        status: {
          value: ['ACTIVE', 'CLOSED'],
          type: SchemaFilterType.STRING,
        },
      };

      if (nameSearch) {
        nextFilters.search = {
          value: [nameSearch],
          type: SchemaFilterType.STRING,
        };
      }

      onFilter({ ...nextFilters });
    }

    localStorage.setItem(
      'individualOrganizationsFilters',
      JSON.stringify({ ...nextValues }),
    );
  };

  const onTableSetPage = (data: { id: string; take: string }) => {
    const nextFilters: Filters = { ...filters };

    if (nameSearch) {
      nextFilters.search = {
        value: [nameSearch],
        type: SchemaFilterType.STRING,
      };
    }

    setPage({ ...data, filters: nextFilters });

    localStorage.setItem(
      'individualOrganizationsFilters',
      JSON.stringify({
        ...filters,
        cursor: { value: [data.id], type: SchemaFilterType.STRING },
      }),
    );
  };

  useEffect(() => {
    if (baseInformation) {
      dispatch(clearGeneralState());
      dispatch(clearTopUpOptions());
    }

    if (generalUserInfo || baseUserInformation) {
      onClear();
    }
  }, [generalUserInfo, baseInformation, baseUserInformation]);

  useEffect(() => {
    if (
      !applicationSchemes &&
      activePermissions?.includes(PermissionEnum.APPLICATIONS_SCHEMES_LIST)
    ) {
      getCustomersApplicationSchemes();
    }
  }, [applicationSchemes, activePermissions]);

  useEffect(() => {
    if (!countriesList && countries) {
      setCountriesList(countries);
    }
  }, [countries, countriesList]);

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !individualCustomers
    ) {
      let currentFilters = filters;
      const currentFiltersStorage = localStorage.getItem(
        'individualOrganizationsFilters',
      );

      if (currentFiltersStorage) {
        currentFilters = JSON.parse(currentFiltersStorage);

        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.search) {
            setNameSearch(parsed.search as string);

            currentFilters.search = {
              value: [parsed.search as string],
              type: SchemaFilterType.STRING,
            };
          }
        }

        if (!currentFilters.type) {
          currentFilters.type = {
            value: ['INDIVIDUAL', 'COMPANY_OFFICIAL'],
            type: SchemaFilterType.STRING,
          };
        }

        if (!currentFilters.status) {
          currentFilters.status = {
            value: ['ACTIVE', 'CLOSED'],
            type: SchemaFilterType.STRING,
          };
        }

        onFilterChange(currentFilters);
      }

      if (!filters.type && !currentFiltersStorage) {
        const filtersValues: Filters = { ...currentFilters };

        filtersValues.type = {
          value: ['INDIVIDUAL', 'COMPANY_OFFICIAL'],
          type: SchemaFilterType.STRING,
        };

        if (!filters.status) {
          filtersValues.status = {
            value: ['ACTIVE', 'CLOSED'],
            type: SchemaFilterType.STRING,
          };
        }

        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.search) {
            setNameSearch(parsed.search as string);

            filtersValues.search = {
              value: [parsed.search as string],
              type: SchemaFilterType.STRING,
            };
          }
        }

        onFilter({ ...filtersValues });
      } else if (!individualCustomers) {
        if (search) {
          const parsed = queryString.parse(search);

          if (parsed.search) {
            setNameSearch(parsed.search as string);

            // eslint-disable-next-line react-hooks/immutability
            currentFilters.search = {
              value: [parsed.search as string],
              type: SchemaFilterType.STRING,
            };
          }
        }

        if (!currentFilters.status) {
          currentFilters.status = {
            value: ['ACTIVE', 'CLOSED'],
            type: SchemaFilterType.STRING,
          };
        }

        getListAction({
          filters: {
            ...currentFilters,
          },
        });
      }
    }
  }, [filters, prevFilters, individualCustomers, search]);

  useEffect(() => {
    if (!individualCustomers && search) {
      formatSearchParams({
        currentSchemaValues: customersListSchema,
        storageKey: 'individualOrganizationsFilters',
        search,
      });
    }
  }, [individualCustomers, search]);

  useEffect(() => {
    const currentFilters = localStorage.getItem(
      'individualOrganizationsFilters',
    );

    if (
      currentFilters &&
      !isEqual(prevPagination, pagination) &&
      !pagination?.hasPreviousPage
    ) {
      const parsed = JSON.parse(currentFilters);

      if (parsed.cursor) {
        delete parsed.cursor;

        localStorage.setItem(
          'individualOrganizationsFilters',
          JSON.stringify({ ...parsed }),
        );
      }
    }
  }, [pagination, prevPagination]);

  useEffect(() => {
    if (countriesList) {
      setValues('countryId', countriesList);
    }
  }, [countriesList]);

  useEffect(() => {
    dispatch(clearIndividualList());
  }, []);

  return {
    nameSearch,
    updateSelectedListVisible,
    setUpdateSelectedListVisible,
    updateAllListVisible,
    setUpdateAllListVisible,
    action,
    setAction,
    popupVisible,
    setPopupVisible,
    isLoading,
    errorList,
    filtersComponent,
    handleSubmit,
    countriesList,
    filters,
    isFetching,
    onCustomerSearch,
    onTableFilterChange,
    onTableSetPage,
  };
};

export default useIndividualList;
